import type { Decompte } from '../partage/api/types';
import type { AnalyseDeLaFeuille, EtatDAnalyse, TempsDUneEtape } from '@nextilt/commun';

/*
 * **Ce que le panneau d'analyse montre des comptes** (chantier theme-analyse, ticket 02 ; maquette
 * `quatre-themes.html`, `panneauAnalyse`, `.barre-n`, `.repart`, `.cmp`). Sans rendu, testé dans
 * `tests/client/projets/lignesDuPanneauDAnalyse.test.ts` : l'écran (`PanneauDAnalyse.tsx`) n'a rien à
 * compter, il lit les comptes du serveur (`Feuille.analyse`) rangés ici.
 */

/** Au-delà, la barre de l'avancement n'a plus de segments : une seule barre, remplie en proportion. */
export const SEGMENTS_MAX = 24;
/** Les lignes d'un bloc long avant « + N autres » (un choix laissé à l'agent, ticket 02). */
export const LIGNES_MAX = 6;

/** La barre de l'avancement : un segment par bulle comptée, ou une seule barre quand il y en a trop. */
export type BarreDeLAvancement = { sorte: 'segments'; pleins: boolean[] } | { sorte: 'barre'; part: number };

export function barreDeLAvancement({ faites, total }: Decompte): BarreDeLAvancement {
  if (total > SEGMENTS_MAX) return { sorte: 'barre', part: faites / total };
  return { sorte: 'segments', pleins: Array.from({ length: total }, (_, i) => i < faites) };
}

/** La répartition par état, puis les idées (à part dans les comptes), sans les états vides. */
export function repartitionDessinee(
  analyse: Pick<AnalyseDeLaFeuille, 'repartition' | 'idees'>
): { etat: EtatDAnalyse; nombre: number }[] {
  return [...analyse.repartition, { etat: 'reflexion' as const, nombre: analyse.idees }].filter((r) => r.nombre > 0);
}

/** Une ligne de « Où passe le temps ». */
export interface LigneDuTemps {
  id: string;
  titre: string;
  /** Ce qui reste ; une étape faite, son tout. */
  minutes: number;
  faite: boolean;
  /** La longueur de sa barre, de 0 à 1, rapportée à la plus longue. */
  longueur: number;
}

/** Les étapes, la plus longue d'abord (dans ton ordre à durée égale), sans celles qui ne prennent rien. */
export function ouPasseLeTemps(etapes: readonly TempsDUneEtape[]): LigneDuTemps[] {
  const lignes = etapes
    .map((e) => ({ id: e.id, titre: e.titre, faite: e.faite, minutes: e.faite ? e.total : e.reste }))
    .filter((l) => l.minutes > 0);
  const plusLongue = Math.max(0, ...lignes.map((l) => l.minutes));
  return lignes
    .map((l, i) => ({ l, i }))
    .sort((x, y) => y.l.minutes - x.l.minutes || x.i - y.i)
    .map(({ l }) => ({ ...l, longueur: l.minutes / plusLongue }));
}

/** Un bloc long : ses premières lignes, et combien d'autres. */
export function enPeu<T>(lignes: readonly T[], max: number = LIGNES_MAX): { montrees: T[]; autres: number } {
  return { montrees: lignes.slice(0, max), autres: Math.max(0, lignes.length - max) };
}

/** Une ligne de « Où passe le temps, par … » (ticket 03) : un groupe, sa durée, sa barre. */
export interface TempsDUnGroupe {
  cle: string;
  nom: string;
  minutes: number;
  /** La longueur de sa barre, de 0 à 1, rapportée au groupe le plus long. */
  longueur: number;
}

/** Le temps comparé entre groupes, dans l'ordre des groupes (comme les colonnes de la carte). */
export function tempsCompare(groupes: readonly { cle: string; nom: string; duree: number }[]): TempsDUnGroupe[] {
  const plusLong = Math.max(0, ...groupes.map((g) => g.duree));
  return groupes.map((g) => ({ cle: g.cle, nom: g.nom, minutes: g.duree, longueur: plusLong > 0 ? g.duree / plusLong : 0 }));
}
